import type { ReactNode } from "react";
import { ArrowLeft, ArrowRight, Check, CircleNotch } from "@phosphor-icons/react";
import { Logo } from "@/components/Logo";
import { Button } from "@/components/ui/button";
import { cn } from "@/lib/utils";
import type { StepKey } from "./types";

export interface StepMeta {
  key: StepKey;
  title: string;
  sub: string;
  icon: ReactNode;
}

interface Props {
  steps: StepMeta[];
  activeIndex: number;
  completion: Record<StepKey, boolean>;
  onJump: (index: number) => void;
  canJumpTo?: (index: number) => boolean;
  title: string;
  deck: string;
  children: ReactNode;
  // nav
  onBack: () => void;
  onPrimary: () => void;
  onSkip?: () => void;          // shown for optional steps
  primaryLabel: string;
  primaryDisabled?: boolean;
  saving?: boolean;
  navigationDisabled?: boolean;
  showBack: boolean;
}

export function OnboardingShell({
  steps, activeIndex, completion, onJump, canJumpTo, title, deck, children,
  onBack, onPrimary, onSkip, primaryLabel, primaryDisabled, saving, showBack,
  navigationDisabled,
}: Props) {
  const pct = Math.round(((activeIndex + 1) / steps.length) * 100);
  const isFinish = steps[activeIndex]?.key === "finish";
  return (
    // Full-screen takeover: the wizard renders inside the therapist dashboard route
    // tree, so a fixed overlay is what lets it escape the dashboard sidebar/banner
    // and own the whole viewport.
    <div className="fixed inset-0 z-50 overflow-y-auto bg-background">
      {/* mobile progress */}
      <div className="sticky top-0 z-20 flex items-center gap-3 border-b border-[oklch(90%_0.02_84)] bg-background/90 px-4 py-3 backdrop-blur md:hidden">
        <Logo className="h-4 w-auto text-primary" />
        <div className="h-1.5 flex-1 overflow-hidden rounded-full bg-muted">
          <div className="h-full bg-primary transition-all" style={{ width: `${pct}%` }} />
        </div>
        <span className="text-xs font-semibold text-muted-foreground">{activeIndex + 1}/{steps.length}</span>
      </div>

      <div className="grid md:grid-cols-[300px_1fr]">
        {/* sidebar */}
        <aside className="sticky top-0 hidden h-screen flex-col bg-[linear-gradient(165deg,var(--color-brand-dark),var(--color-brand))] p-8 text-white md:flex">
          <Logo
            className="h-5 w-auto text-white"
            style={{ ['--color-brand' as string]: '#fff' } as React.CSSProperties}
          />
          <nav className="mt-12 flex flex-1 flex-col gap-1">
            {steps.map((s, i) => {
              const active = i === activeIndex;
              const done = completion[s.key] && !active;
              const canJump = canJumpTo ? canJumpTo(i) : true;
              const disabled = navigationDisabled || !canJump;
              return (
                <button
                  key={s.key}
                  onClick={() => {
                    if (!disabled) onJump(i);
                  }}
                  disabled={disabled}
                  aria-disabled={disabled}
                  title={canJump ? undefined : "Dovrši prethodne korake prije ovog koraka."}
                  className={cn(
                    "flex w-full items-start gap-3 rounded-xl px-3 py-2.5 text-left transition-colors",
                    active ? "bg-white/15" : "hover:bg-white/8",
                    !canJump && "cursor-not-allowed opacity-55 hover:bg-transparent"
                  )}
                >
                  <span
                    className={cn(
                      "grid size-8 shrink-0 place-items-center rounded-lg border text-[17px]",
                      active
                        ? "border-[var(--color-accent-gold)] bg-[var(--color-accent-gold)] text-[var(--color-brand-dark)]"
                        : done
                          ? "border-white/30 bg-white/15 text-white"
                          : "border-white/25 text-white/70"
                    )}
                  >
                    {done ? <Check weight="bold" className="size-4" /> : s.icon}
                  </span>
                  <span>
                    <span className="block text-sm font-semibold">{s.title}</span>
                    <span className="block text-xs text-white/60">{s.sub}</span>
                  </span>
                </button>
              );
            })}
          </nav>
          <p className="text-xs leading-relaxed text-white/55">
            Sve možeš promijeniti kasnije u Postavkama profila. Napredak se sprema automatski.
          </p>
        </aside>

        {/* main */}
        <main className="mx-auto w-full max-w-3xl px-5 pb-28 pt-10 md:px-12">
          <h1 className="text-2xl font-semibold tracking-tight sm:text-3xl" style={{ fontFamily: "var(--font-heading)" }}>
            {title}
          </h1>
          <p className="mt-2 max-w-xl text-sm leading-relaxed text-muted-foreground">{deck}</p>
          <div className="mt-8">{children}</div>

          <div className="mt-8 flex items-center justify-between gap-3 border-t border-[oklch(90%_0.02_84)] pt-6">
            <Button
              variant="ghost"
              size="lg"
              onClick={onBack}
              className={cn("text-muted-foreground hover:text-foreground", !showBack && "invisible")}
            >
              <ArrowLeft weight="bold" className="size-4" />
              Nazad
            </Button>
            <div className="flex items-center gap-2.5">
              {onSkip && (
                <Button variant="outline" size="lg" onClick={onSkip}>
                  Preskoči
                </Button>
              )}
              <Button size="lg" onClick={onPrimary} disabled={primaryDisabled || saving} className="min-w-44">
                {primaryLabel}
                {saving ? (
                  <CircleNotch className="size-4 animate-spin" />
                ) : isFinish ? (
                  <Check weight="bold" className="size-4" />
                ) : (
                  <ArrowRight weight="bold" className="size-4" />
                )}
              </Button>
            </div>
          </div>
        </main>
      </div>
    </div>
  );
}
